<template>
    <b-container>
        <b-row>
            <b-col cols="12">
                <CuponDescuentoComponent
                    :cart_id="cart_id"
                    :loading="loading"
                    :esMovil="esMovil"
                    @setDescuento="setDescuento"
                    @desactivarCuponGeneral="desactivarCuponGeneral"
                />
            </b-col>
            <b-col cols="12" md="8" class="mt-3">
                <ul v-for="(item, index) in items" :key="index" class="resumen d-md-flex">
                    <li class="altcolor-content">
                        <div class="text-bold oswald text-uppercase m-0 w-100">
                            {{ index + 1 }}. {{ item.precio.amount | formatear_precio }}
                        </div>
                    </li>
                    <li>
                        <div class="h5 text-bold oswald text-uppercase m-0 w-100">
                            {{ item.zona.name }}
                        </div>
                    </li>
                    <li class="flex-grow-1">
                        <div class="h5 text-bold oswald text-uppercase m-0 w-100">
                            {{ $t('general.fila') }}: {{ item.row }} - {{ $t('general.butaca') }}: {{ item.seat }}
                        </div>
                    </li>
                    <li class="text-bold pr-0 pl-0">
                        <label class="h5 pr-0">{{ $t('general.precio') }}</label>
                        <div class="h5 m-lg-0">
                            <template v-if="item.discount && hasPercentageDiscount(item.discount)">
                                <span>
                                    {{ getTotal(item) | formatear_precio }}
                                </span>
                                <span>
                                    <small>(<del>{{ item.precio.amount | formatear_precio }}</del>)</small>
                                </span>
                            </template>
                            <template v-else>
                                {{ getTotal(item) | formatear_precio }}
                            </template>
                        </div>
                        <template v-if="item.discount">
                            <div class="text-small text-success pt-1">
                                {{ item.discount.name }}
                            </div>
                        </template>                        
                    </li>
                </ul>
                <div class="mb-5">
                    <label>{{ $t('evento.total_pagar') }}</label>
                    <div class="price price-large gradient-color">
                        {{ total_con_gastos | formatear_precio }}
                    </div>
                </div>
            </b-col>

            <b-col cols="12">
                <FormTerminos
                    :error="error"
                    :desactivar="loading"
                    @continuar="aceptarCondiciones"
                />
            </b-col>

            <ResumenPrecioComponent
                :price="total_sin_gastos"
                :price_management="total_gastos"
                :price_management_interno="items[0]?.precio?.price_management_interno"
                :iva="footer.iva_partido"
            />

            

            <b-col cols="12" class="mt-3 d-flex justify-content-start flex-wrap">
                <button 
                    id="resumen-compra-responsive" 
                    :class="['button', 'effect-button', { 'large-button': !esMovil }]" 
                    :disabled="(formulario && pagar) || loading"
                    @click.prevent="terminar"
                >
                    <span>{{ $t('partido.continuar') }}</span>
                </button>

                <button
                    id="atras-resumen"
                    type="button"
                    :disabled="(formulario && pagar) || loading" 
                    :class="['button', 'effect-button', 'ml-2', 'compra-responsive', { 'large-button': !esMovil }]"
                    @click="volver()"
                >
                    <span>{{ $t('formularios.atras') }}</span>
                </button>

                <template v-if="formulario && pagar">
                    <div id="formulario-compra" v-html="formulario" v-show="false"></div>
                </template>
            </b-col>
        </b-row>
    </b-container>
</template>

<script>
    import CuponDescuentoComponent from './CuponDescuentoComponent';
    import FormTerminos from '@/components/frontend/components/FormTerminos';
    import ResumenPrecioComponent from '@/components/frontend/compra/components/ResumenPrecioComponent';
    import { useEventoTracking } from '@/composables/front/analytics/useEventoTracking';
    import { useAbonoTracking } from '@/composables/front/analytics/useAbonoTracking';

    export default {
        name: 'ResumenCompraComponent',
        props: {
            items: {
                type: Array,
                required: true
            },
            event: {
                type: Object,
                default: null
            },
            ga4Category: {
                type: String,
                default: null
            },
            jet_id: {
                type: String,
                default: null
            },
            formulario: {
                type: String,
                default: null
            },
            cart_id: {
                type: [String, Number],
                default: null
            }
        },
        data() {
            return {
                pagar: false,
                error: false,
                loading: false,
                terminos: false,
                privacidad: false,
                condiciones: false,
                comunicaciones: false,
                descuentos_condicionales: []
            };
        },
        created() {
            this.getDescuentosCondicionales();
        },
        computed: {
            esMovil() {
                return window.innerWidth <= 768;
            },
            lista_abonados() {
                return this.$store.getters.getListaAbonados;
            },
            teatro() {
                return this.$store.getters.getTeatro;
            },
            footer() {
                return this.$store.getters.getFooter;
            },
            total_con_gastos() {
                return this.items.reduce((acc, item) => acc + this.getTotal(item, true), 0);
            },
            total_sin_gastos() {
                return this.items.reduce((acc, item) => acc + this.getTotal(item, false), 0);
            },
            total_gastos() {
                return this.items.reduce((acc, item) => {
                    const p = item.precio;
                    return !p.price_management_interno ? acc + p.price_management : acc;
                }, 0);
            }
        },
        methods: {
            trackPaymentInfo() {
                try {
                    // Determine if this is an evento or abono based on evento prop
                    const isEvento = !!this.event;
                    
                    if (isEvento) {
                        const { trackAddPaymentInfo } = useEventoTracking({ store: this.$store, getVuexAlias: this.config.getVuexAlias });

                        trackAddPaymentInfo({
                            evento_id: this.event?.id,
                            evento_name: this.event?.nombre || this.event?.name,
                            datos_compradores: (this.items || []).map(item => ({
                                precio: parseFloat(item.precio?.amount || item.price || 0)
                            })),
                            productos: [],
                            total_price: this.total_con_gastos,
                            moneda: 'EUR'
                        });
                    } else {
                        const { trackAddPaymentInfo } = useAbonoTracking({ store: this.$store, getVuexAlias: this.config.getVuexAlias });

                        trackAddPaymentInfo({
                            abono_id: this.lista_abonados?.id,
                            abono_name: this.lista_abonados?.nombre,
                            lista_abonados: [this.lista_abonados],
                            resumen_total: this.total_con_gastos,
                            moneda: 'EUR'
                        }, '', null, this.items);
                    }
                } catch (error) {
                    console.error('[GA4] add_payment_info failed:', error);
                }
            },
            async terminar() {
                this.loading = true;

                if (!this.terminos) {
                    this.error = true;
                    this.loading = false;
                    return;
                }

                const exito = await this.guardarTerminosCondiciones();
                if (!exito) return;

                this.trackPaymentInfo();

                this.pagar = true;

                if (this.formulario && this.pagar) {
                    setTimeout(() => {
                        const form = document.querySelector('#formulario-compra form');
                        if (form) form.submit();
                    }, 500);
                } else {
                    this.$emit('terminado');
                }

                this.loading = false;
            },
            getTotal(item, con_gastos = false) {
                let precio = item.precio.amount;

                if (item.discount && this.hasPercentageDiscount(item.discount)) {
                    // SGA shape: { valueType: 'PERCENTAGE', value: 20 }
                    // Legacy shape: { percentage: 10 }
                    const pct = item.discount.valueType === 'PERCENTAGE'
                        ? item.discount.value
                        : item.discount.percentage;
                    precio = precio - (precio * pct / 100);
                }

                if (con_gastos && !item.precio.price_management_online) {
                    precio += item.precio.price_management;
                }

                return precio;
            },
            hasPercentageDiscount(discount) {
                if (!discount) return false;
                if (discount.valueType === 'PERCENTAGE' && typeof discount.value === 'number') return true;
                if (typeof discount.percentage === 'number' && discount.percentage > 0) return true;
                return false;
            },
            aceptarCondiciones(continuar, privacidad, condiciones, comunicaciones) {
                this.terminos = continuar;
                this.privacidad = privacidad;
                this.condiciones = condiciones;
                this.comunicaciones = comunicaciones;
            },
            async guardarTerminosCondiciones() {
                try {
                    await this.$store.dispatch(this.config.getVuexAlias('sga.cart.guardar-condiciones'), {
                        filters: {},
                        cart_id: this.cart_id,
                        privacidad: this.privacidad,
                        condiciones: this.condiciones,
                        comunicaciones: this.comunicaciones
                    });
                    return true;
                } catch (error) {
                    this.$toast.error('No se han podido guardar las condiciones: ' + error.response?.data?.message);
                    this.loading = false;
                    return false;
                }
            },
            getDescuentosCondicionales() {
                this.loading = true;

                this.$store.dispatch(this.config.getVuexAlias('sga.cart.get.descuentos'), {
                    cart_id: this.cart_id,
                }).then(res => {
                    this.descuentos_condicionales = res.data.data;

                    let applied = false;
                    const appliedLicensorIds = new Set();

                    this.descuentos_condicionales.forEach(descuento => {
                        let descuento_disponible = descuento.discounts?.find(d => {
                            // New shape: licensorEntitlements is an array of { id } (or null).
                            // Legacy shape: licensorEntitlementId is a single id.
                            const licensorIds = d.licensorEntitlements
                                ? d.licensorEntitlements.map(le => le.id).filter(Boolean)
                                : (d.licensorEntitlementId ? [d.licensorEntitlementId] : []);
                            return d.limit > 0 && !licensorIds.some(id => appliedLicensorIds.has(id));
                        });

                        if(!descuento_disponible) return;

                        this.items.forEach(item => {
                            if (!item.discount && item.id === descuento.cart_item_sga_id) {
                                this.$set(item, 'discount', {
                                    id: descuento_disponible.id,
                                    name: descuento_disponible.name,
                                    value: descuento_disponible.value,
                                    valueType: descuento_disponible.valueType,
                                    code: descuento_disponible.code,
                                    licensorEntitlements: descuento_disponible.licensorEntitlements
                                });
                                applied = true;
                                const licensorIds = (descuento_disponible.licensorEntitlements || [])
                                    .map(le => le.id).filter(Boolean);
                                licensorIds.forEach(id => appliedLicensorIds.add(id));
                            }
                        });
                    });

                    if (applied) {
                        this.guardarDatosCupon();
                    }
                }).finally(() => {
                    this.loading = false;
                });
            },
            setDescuento(descuento) {
                this.items.forEach(item => {
                    this.$set(item, 'discount', null);
                });

                if(descuento) {
                    this.items.forEach((item, index) => {
                        if (index < descuento.limit) {
                            this.$set(item, 'discount', {
                                id: descuento.id,
                                name: descuento.name,
                                value: descuento.value,
                                valueType: descuento.valueType,
                                code: descuento.code,
                                licensorEntitlementId: descuento.licensorEntitlementId
                            });
                        }
                    });
                }

                this.guardarDatosCupon();
            },
            volver() {
                this.items.forEach(item => {
                    delete item.discount;
                });

                this.$emit('volver');
            },
            guardarDatosCupon() {
                this.loading = true;

                const params = {
                    cart_id: this.cart_id,
                    items: this.items.map(entrada => {
                        // New API expects items[].discounts (plural, array of objects).
                        // Each discount has { id, value, valueType, name, code|null }.
                        const discounts = entrada.discount
                            ? [{
                                id: entrada.discount.id,
                                value: entrada.discount.value,
                                valueType: entrada.discount.valueType,
                                name: entrada.discount.name,
                                code: entrada.discount.code ?? null
                            }]
                            : [];

                        return {
                            id: entrada.id,
                            discounts
                        };
                    })
                };

                this.$store.dispatch(this.config.getVuexAlias('sga.cart.descuentos-codigo.guardar'), params).then(() => {
                    this.solicitarDatosCobro();
                }).catch(() => {

                });
            },
            solicitarDatosCobro() {
                const params = {
                    filters: {},
                    cart_id: this.cart_id
                }

                this.$store.dispatch(this.config.getVuexAlias('sga.cart.solicitar-cobro'), params).then(res => {
                    this.$emit('setDatosCobro', res.data.jet_id, res.data.formulario, res.data.session_id, res.data.cart_id);
                }).finally(() => {
                    this.loading = false;
                });
            },
            desactivarCuponGeneral() {
                this.items.forEach(item => {
                    this.$set(item, 'discount', null);
                });

                this.getDescuentosCondicionales();
            }
        },
        components: {
            FormTerminos,
            ResumenPrecioComponent,
            CuponDescuentoComponent
        }
    };
</script>
